import React, { useEffect, useState } from 'react';
import { TextInput } from '../../Shared/Component/forms/TextInput';
import { RadioGroupParametriz } from '../../Shared/Component/forms/RadioGroupParametriz';
import { CreateUserRequest } from '../Types/UsersType';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { getEmailValidationError, getPasswordValidationError } from '../../Shared/Utils/validationPolicy';
import { useValidationPolicy } from '../../Shared/Hooks/useValidationPolicy';
import { PasswordPolicyHints } from '../../Shared/Component/forms/PasswordPolicyHints';
import { beginRegistration, completeRegistration, verifyRegistrationOtp } from '../Services/RegistrationService';
import { getAttribution } from '../../Shared/Services/VisitorTracker';
import { PasswordInput } from '../../Shared/Component/forms/PasswordInput';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useParams } from 'react-router-dom';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';
import { useAnalytics } from '../../Shared/Context/AnalyticsContext';
import { UserEarlyAccessResponse } from '../../LandingScreens/Types/EarlyAccessType';

type Step = 'EMAIL' | 'OTP' | 'PASSWORD';

const STEPS_CONFIG = [
  { id: 'EMAIL', label: 'Registration' },
  { id: 'OTP', label: 'Verification' },
  { id: 'PASSWORD', label: 'Complete' }
];

interface RegistrationComponentProps {
  data: UserEarlyAccessResponse;
}



// function RegistrationComponent() {
  const EarlyAccessRegistrationComponent = ({
    data,
  }: RegistrationComponentProps) => {
  const { values, handleInputChange, handleRequiredValueChange } = useFormController<CreateUserRequest>({
    email: "",
    password: "",
    role: "",
    otp: "",
    firstName: "",
    lastName: "",
    linkedInUrl: "",
    inviteCode: "",
    isFoundingMember2026:false
  });


  const [step, setStep] = useState<Step>('EMAIL');
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const { inviteCode } = useParams<{ inviteCode: string }>();
  const redirect = useRedirect();
  const { track } = useAnalytics();
  const { policy } = useValidationPolicy();

  useEffect(() => {
    track('registration_started');
  }, [track]);

useEffect(() => {
  if (inviteCode) {
    // values.inviteCode = inviteCode;
    handleRequiredValueChange("inviteCode")(inviteCode || "");
  }
}, [inviteCode]);

  const getStepStatus = (currentStepId: string) => {
    const order = ['EMAIL', 'OTP', 'PASSWORD'];
    const currentIndex = order.indexOf(step);
    const itemIndex = order.indexOf(currentStepId);
    if (currentIndex > itemIndex) return 'complete';
    if (currentIndex === itemIndex) return 'active';
    return 'pending';
  };



  const handleSendOtp = async () => {
    const emailError = getEmailValidationError(values.email, policy);
    if (emailError) {
      setAlert({
        variant: "error",
        title: "Invalid Email",
        message: emailError,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      return;
    }

    const attribution = getAttribution();
    const response = await beginRegistration({
      email: values.email,
      referralCode: attribution?.referralCode,
      landingPage: attribution?.landingPage || window.location.pathname,
    });
    if (response.success) {
      setStep('OTP');
    } else {
      setAlert({
        variant: "error",
        title: "Registration Failed",
        message: response.message,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
    }
  };

  const handleVerifyOtp = async () => {
    const response = await verifyRegistrationOtp({ email: values.email, otp: values.otp });
    if (response.success) {
      setAlert({ variant: "success", title: "Identity Verified", message: "Security check complete." });
      setStep('PASSWORD');
    } else {
      setAlert({ variant: "error", title: "Verification Failed", message: response.message });
    }
  };

  const handleSubmit = async () => {
    const passwordError = getPasswordValidationError(values.password, policy);
    if (passwordError) {
      setAlert({
        variant: "error",
        title: "Weak Password",
        message: passwordError,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      return;
    }

    const payload = {
      email: values.email,
      password: values.password,
      role: values.role,
      firstName: values.firstName,
      lastName: values.lastName,
      linkedInUrl: values.linkedInUrl,
      inviteCode: inviteCode,
      isFoundingMember2026: data.foundingMember2026
    };
    const response = await completeRegistration(payload);

    if (response.success) {
      track('registration_completed', { userId: response?.data?.id });
      setAlert({
        variant: "success",
        title: "Welcome Aboard",
        message: response.message,
      });
      setTimeout(() => {
        redirect("/login");
      }, 5000);
    } else {
      setAlert({
        variant: "error",
        title: "Registration Failed",
        message: response.message,
      });
    }
  };

  useEffect(() => {
    if (data) {
      handleRequiredValueChange("email")(data.email || "");
      handleRequiredValueChange("firstName")(data.firstName || "");
      handleRequiredValueChange("lastName")(data.lastName || "");
      handleRequiredValueChange("isFoundingMember2026")(data.foundingMember2026 || "");
    }
  }, [data]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-4 py-12 selection:bg-cyan-500/30 selection:text-cyan-200">
      
      {alert && (
            <div className="mb-6">
              <Alert {...alert} onClose={() => setAlert(null)} />
            </div>
          )}
      
      <div className="max-w-md w-full">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-indigo-400 mb-3 drop-shadow-[0_2px_10px_rgba(34,211,238,0.2)]">
            The Executive Network
          </h1>
          <p className="text-slate-400 text-sm font-medium tracking-wide">
            Elite leadership meets <span className="text-cyan-400">exclusive opportunities</span>.
          </p>
        </div>

        {/* Added '[&_label]:text-white' to force all nested label elements to be white */}
        <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-[2.5rem] shadow-[0_0_50px_-12px_rgba(6,182,212,0.15)] p-10 relative overflow-hidden [&_label]:text-white [&_label]:font-medium">
          {/* Subtle Cyber Glow Top Border */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-60" />
          
          {/* FIXED STEPPER UI */}
          <nav aria-label="Progress" className="relative mb-12">
            <div className="flex items-start justify-between">
              {STEPS_CONFIG.map((s, idx) => {
                const status = getStepStatus(s.id);
                return (
                  <div key={s.id} className="flex flex-col items-center flex-1 relative">
                    {/* Circle Indicator */}
                    <div className={`
                      w-10 h-10 flex items-center justify-center rounded-full border-2 transition-all duration-500 z-10
                      ${status === 'complete' ? 'bg-gradient-to-r from-emerald-400 to-teal-500 border-emerald-400 text-slate-950 shadow-[0_0_15px_rgba(52,211,153,0.4)] font-bold' : 
                        status === 'active' ? 'bg-slate-950 border-cyan-400 text-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.35)] shadow-cyan-400/20' : 
                        'bg-slate-950 border-slate-800 text-slate-600'}
                    `}>
                      {status === 'complete' ? (
                        <CheckIcon className="w-5 h-5 stroke-[3]" />
                      ) : (
                        <span className="text-sm font-bold">{idx + 1}</span>
                      )}
                    </div>

                    {/* Label (Using inline text styling here so it skips the container override) */}
                    <span className={`mt-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-500
                      ${status === 'active' ? 'text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.5)]' : 'text-slate-500'}`}>
                      {s.label}
                    </span>

                    {/* Connector Line */}
                    {idx < STEPS_CONFIG.length - 1 && (
                      <div className="absolute top-5 left-[calc(50%+20px)] w-[calc(100%-40px)] h-[2px] bg-slate-800">
                        <motion.div 
                          initial={{ width: "0%" }}
                          animate={{ width: status === 'complete' ? "100%" : "0%" }}
                          className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          <div className="mt-4">
            <AnimatePresence mode="wait">
              {step === 'EMAIL' && (
                <motion.div
                  key="email"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="space-y-6"
                >
                  <TextInput
                    label="Professional Email"
                    name="email"
                    type="email"
                    placeholder="name@company.com"
                    value={values.email}
                    onChange={handleInputChange("email")}
                    readOnly
                  />
                  <button
                    onClick={handleSendOtp}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-black tracking-wide py-4 rounded-2xl transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] border border-cyan-300/20"
                  >
                    Get Access Code
                  </button>
                </motion.div>
              )}

              {step === 'OTP' && (
                <motion.div
                  key="otp"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-6"
                >
                  <TextInput
                    label="Verification Code"
                    name="otp"
                    value={values.otp}
                    onChange={handleInputChange("otp")}
                    placeholder="Enter 4-digit OTP"
                  />
                  <button
                    onClick={handleVerifyOtp}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-black tracking-wide py-4 rounded-2xl transition-all shadow-lg shadow-cyan-500/20"
                  >
                    Verify Account
                  </button>
                </motion.div>
              )}

              {step === 'PASSWORD' && (
                <motion.div
                  key="password"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >

                  <TextInput
                    label="First Name"
                    name="firstName"
                    placeholder="Enter your first name"
                    value={values.firstName}
                    onChange={handleInputChange("firstName")}
                    readOnly
                  />

                  <TextInput
                    label="Last Name"
                    name="lastName"
                    placeholder="Enter your last name"
                    value={values.lastName}
                    onChange={handleInputChange("lastName")}
                    readOnly
                  />

                   <TextInput
                    label="LinkedIn Profile URL"
                    name="linkedInUrl"
                    placeholder="Enter your LinkedIn profile URL"
                    value={values.linkedInUrl}
                    onChange={handleInputChange("linkedInUrl")}
                  />

                  <PasswordInput
                    label="Create Secure Password"
                    name="password"
                    placeholder="Min. 8 characters"
                    value={values.password}
                    onChange={handleInputChange("password")}
                  />

                  <PasswordPolicyHints password={values.password} policy={policy} />

                  <RadioGroupParametriz
                    label="Your Professional Intent"
                    options={
                      data.role === "candidate"
                        ? [
                            {
                              label: "Executive (10+ Years Exp)",
                              value: "CANDIDATE",
                            },
                          ]
                        : data.role === "super_admin" ||
                          data.role === "delegated_admin"
                        ? [
                            {
                              label: "Hiring Partner (CEO/MD)",
                              value: "RECRUITER",
                            },
                          ]
                        : [
                            {
                              label: "Executive (10+ Years Exp)",
                              value: "CANDIDATE",
                            },
                            {
                              label: "Hiring Partner (CEO/MD)",
                              value: "RECRUITER",
                            },
                          ]
                    }
                    value={values.role}
                    onChange={handleRequiredValueChange("role")}
                  />

                  <button
                    onClick={handleSubmit}
                    className="w-full bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-indigo-500 hover:opacity-95 text-white font-black tracking-wide py-4 rounded-2xl transition-all shadow-xl shadow-fuchsia-500/20 border border-white/10"
                  >
                    Complete Registration
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-8 text-center text-slate-600 text-xs font-semibold tracking-wider uppercase">
          &copy; 2026 Reverse Job Portal. <br/>
          <span className="text-slate-500/80">Secure & Encrypted Communication.</span>
        </div>
      </div>
    </div>
  );
}

export default EarlyAccessRegistrationComponent;